/**
 * sanitize.ts
 * -----------
 * XSS sanitization utilities powered by DOMPurify.
 *
 * Two modes:
 *   sanitizeText(str)  – strips ALL HTML → plain text (for names, titles, etc.)
 *   sanitizeHtml(str)  – keeps a safe HTML subset (for rich text, email bodies, etc.)
 *
 * Usage:
 *   import { sanitizeText, sanitizeHtml } from '@/utils/sanitize';
 */

import DOMPurify, { Config } from 'dompurify';

// ── Config ────────────────────────────────────────────────────────────────────

/** Tags allowed in "safe HTML" mode (rich descriptions, email templates, etc.) */
const SAFE_HTML_TAGS = [
  'b', 'i', 'u', 'strong', 'em', 'mark', 'small', 's', 'del', 'ins', 'sub', 'sup', 'strike', 'font',
  'p', 'br', 'hr',
  'ul', 'ol', 'li',
  'a', 'img', 'figure', 'figcaption',
  'video', 'audio', 'source',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'pre', 'code',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col', 'caption',
  'div', 'span',
];

/** Attributes allowed in safe HTML mode (everything the rich-text editor writes: direction, alignment, sizes…) */
const SAFE_HTML_ATTRS = [
  'href', 'src', 'alt', 'title', 'class', 'id',
  'width', 'height', 'style', 'dir', 'lang',
  'target', 'rel',
  'colspan', 'rowspan', 'span', 'start', 'type',
  'controls', 'poster', 'preload', 'muted', 'loop', 'playsinline',
  'data-align', 'data-color', 'color', 'size',
];

/** Embeds (iframes) on top of the safe subset — for content written by a course's authors (a Page activity). */
const RICH_EXTRA_TAGS = ['iframe'];
const RICH_EXTRA_ATTRS = ['allow', 'allowfullscreen', 'frameborder', 'loading', 'referrerpolicy'];
// An embed may only show an https page (http only from a local test server); it never navigates the LMS window.
const EMBED_SRC = /^(https:\/\/|http:\/\/(localhost|127\.0\.0\.1)[:/])/i;
const EMBED_SANDBOX = 'allow-scripts allow-same-origin allow-popups allow-forms allow-presentation';

const PLAIN_TEXT_CONFIG: Config = {
  ALLOWED_TAGS:  [],
  ALLOWED_ATTR:  [],
  KEEP_CONTENT:  true,
};

const SAFE_HTML_CONFIG: Config = {
  ALLOWED_TAGS: SAFE_HTML_TAGS,
  ALLOWED_ATTR: SAFE_HTML_ATTRS,
  FORBID_CONTENTS: ['script', 'style', 'iframe', 'object', 'embed'],
  FORCE_BODY: true,
};

const RICH_HTML_CONFIG: Config = {
  ALLOWED_TAGS: [...SAFE_HTML_TAGS, ...RICH_EXTRA_TAGS],
  ALLOWED_ATTR: [...SAFE_HTML_ATTRS, ...RICH_EXTRA_ATTRS],
  FORBID_CONTENTS: ['script', 'style', 'object', 'embed'],
  FORBID_ATTR: ['srcdoc'],
  FORCE_BODY: true,
};

// Initialise DOMPurify hooks once — strip dangerous URL schemes from href/src (whitespace and control
// characters inside the scheme, e.g. "java\tscript:", are ignored by browsers, so they are ignored here too)
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  const dangerous = /^(javascript|data|vbscript):/i;
  // eslint-disable-next-line no-control-regex -- control characters are exactly what is stripped
  const flat = (v: string | null) => (v || '').replace(/[\u0000-\u0020]/g, '');
  if (node.hasAttribute('href') && dangerous.test(flat(node.getAttribute('href')))) {
    node.removeAttribute('href');
  }
  if (node.hasAttribute('src')) {
    const src = flat(node.getAttribute('src'));
    // Inline raster images are harmless; any other data:/javascript:/vbscript: source is dropped.
    const inlineImage = node.nodeName === 'IMG' && /^data:image\/(png|jpe?g|gif|webp|bmp);/i.test(src);
    if (dangerous.test(src) && !inlineImage) node.removeAttribute('src');
  }
  if (node.hasAttribute('poster') && dangerous.test(flat(node.getAttribute('poster')))) {
    node.removeAttribute('poster');
  }
  if (node.nodeName === 'IFRAME') {
    const src = flat(node.getAttribute('src'));
    if (!EMBED_SRC.test(src)) {
      node.parentNode?.removeChild(node);
      return;
    }
    node.setAttribute('sandbox', EMBED_SANDBOX);
    node.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    if (!node.hasAttribute('loading')) node.setAttribute('loading', 'lazy');
  }
  // Force external links to be safe
  if (node.hasAttribute('target')) {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

// ── Public helpers ────────────────────────────────────────────────────────────

/**
 * Strip ALL HTML — use for names, titles, IDs, search queries, etc.
 * Returns a plain string with zero markup.
 */
export function sanitizeText(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return (DOMPurify.sanitize(str, PLAIN_TEXT_CONFIG) as unknown) as string;
}

/**
 * Keep a curated safe HTML subset — use for descriptions, email bodies,
 * custom CSS sections, footer text, etc.
 * Strips <script>, event handlers, javascript: URLs.
 */
export function sanitizeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return (DOMPurify.sanitize(str, SAFE_HTML_CONFIG) as unknown) as string;
}

/**
 * The safe subset plus embeds (https iframes, sandboxed so they can't navigate or reach the LMS) — for content a
 * course's authors write, such as a Page activity. Scripts, event handlers and script URLs never survive.
 */
export function sanitizeRichHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return (DOMPurify.sanitize(str, RICH_HTML_CONFIG) as unknown) as string;
}

/**
 * The text of an HTML string, for previews and lists. Parsed in an inert document (DOMParser): nothing loads and no
 * handler runs — unlike `div.innerHTML = html`, where `<img src=x onerror=…>` fires even on a detached element.
 */
export function htmlToText(html: string | null | undefined): string {
  if (!html) return '';
  try {
    const body = new DOMParser().parseFromString(String(html), 'text/html').body;
    body.querySelectorAll('script,style,noscript,template').forEach((el) => el.remove());
    return body.textContent || '';
  } catch {
    return String(html).replace(/<[^>]*>/g, '');
  }
}

/**
 * Recursively sanitize all string leaves of an arbitrary object / array
 * returned from the API.  Used by the Axios response interceptor.
 *
 * Leaves non-string primitive values and null/undefined untouched.
 * Does NOT parse multilang tags — that happens only at the display layer
 * so that the raw tagged data is available when editing.
 */
export function sanitizeDeep<T>(value: T): T {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return sanitizeText(value) as unknown as T;
  if (Array.isArray(value))      return value.map(sanitizeDeep) as unknown as T;
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = sanitizeDeep(v);
    }
    return out as T;
  }
  return value;
}
